import { useTranslations } from '@aikoa/i18n/react'
import { type PropsWithChildren, useState } from 'react'
import { View } from 'react-native'
import { Heading3 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button, ButtonLabel, type ButtonSize, type ButtonVariant } from '~/components/ui/button'
import { cn } from '~/lib/utils'

type InlineEditSheetProps = PropsWithChildren<{
  modalKey: string
  title: string
  triggerLabel: string
  onSave: () => void
  onCancel?: () => void
  isSaving?: boolean
  isDisabled?: boolean
  className?: string
  triggerClassName?: string
  triggerVariant?: ButtonVariant
  triggerSize?: ButtonSize
}>

export function InlineEditSheet({
  modalKey,
  title,
  triggerLabel,
  onSave,
  onCancel,
  isSaving,
  isDisabled,
  className,
  triggerClassName,
  triggerVariant = 'outline',
  triggerSize = 'default',
  children,
}: InlineEditSheetProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslations('common')

  const handleSave = () => {
    onSave()
    setIsOpen(false)
  }

  const handleCancel = () => {
    onCancel?.()
    setIsOpen(false)
  }

  return (
    <>
      <Button
        onPress={() => setIsOpen(true)}
        disabled={isDisabled}
        variant={triggerVariant}
        size={triggerSize}
        className={cn(triggerClassName)}>
        {triggerLabel}
      </Button>

      <BottomSheet
        modalKey={modalKey}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        showHandle
        className={cn('px-6 pb-8', className)}>
        <View className="gap-6 pt-2">
          <Heading3 className="text-center">{title}</Heading3>
          <View className="gap-4">{children}</View>
          <View className="flex-row gap-3 pt-2">
            <Button variant="ghost" size="lg" onPress={handleCancel} className="flex-1">
              <ButtonLabel>{t(($) => $.actions.cancel)}</ButtonLabel>
            </Button>
            <Button
              variant="primary"
              size="lg"
              onPress={handleSave}
              loading={isSaving}
              disabled={isSaving}
              className="flex-1">
              <ButtonLabel>{t(($) => $.actions.save)}</ButtonLabel>
            </Button>
          </View>
        </View>
      </BottomSheet>
    </>
  )
}
